import React from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../lib/brand";
import {
  LayoutDashboard,
  Briefcase,
  Scale,
  Handshake,
  PenLine,
  CalendarClock,
  Gavel,
  FileText,
  Folder,
  Users,
  BookOpen,
  CheckSquare,
  Sparkles,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Compass,
  GitCompare,
  Store,
  Eye,
  Video,
  Inbox,
} from "lucide-react";
import { Button } from "../components/ui/button";
import CommandPalette from "../components/CommandPalette";

// Primary workspace nav — the Legal OS surface
const primaryNav = [
  { to: "/app", label: "Home", icon: LayoutDashboard, end: true, testId: "nav-home" },
  { to: "/app/matters", label: "Matters", icon: Briefcase, testId: "nav-matters" },
  { to: "/app/inbox", label: "Inbox", icon: Inbox, testId: "nav-inbox" },
  { to: "/app/agreements", label: "Agreements", icon: Handshake, testId: "nav-agreements" },
  { to: "/app/signatures", label: "Signatures", icon: PenLine, testId: "nav-signatures" },
  { to: "/app/obligations", label: "Obligations", icon: CalendarClock, testId: "nav-obligations" },
  { to: "/app/calendar", label: "Calendar", icon: CalendarClock, testId: "nav-calendar" },
  { to: "/app/disputes", label: "Disputes", icon: Gavel, testId: "nav-disputes" },
  { to: "/app/cases", label: "Cases", icon: Scale, testId: "nav-cases" },
];

// Secondary — resources
const libraryNav = [
  { to: "/app/case-starters", label: "Case Starters", icon: Sparkles, testId: "nav-case-starters" },
  { to: "/app/cases/templates", label: "Case filing", icon: Compass, testId: "nav-case-filing" },
  { to: "/app/templates", label: "Templates", icon: FileText, testId: "nav-templates" },
  { to: "/app/agreements/compare", label: "Compare", icon: GitCompare, testId: "nav-compare" },
  { to: "/app/marketplace", label: "Marketplace", icon: Store, testId: "nav-marketplace" },
  { to: "/app/drafts", label: "Drafts", icon: BookOpen, testId: "nav-drafts" },
  { to: "/app/documents", label: "Documents", icon: Folder, testId: "nav-documents" },
  { to: "/app/contacts", label: "Contacts", icon: Users, testId: "nav-contacts" },
  { to: "/app/tasks", label: "Deadlines", icon: CheckSquare, testId: "nav-tasks" },
  { to: "/app/learn", label: "Learn", icon: BookOpen, testId: "nav-learn" },
];

// Add-ons — everything gated behind an add-on lives here
const addonNav = [
  { to: "/app/nyaya", label: "Nyaya Drishti", icon: Eye, testId: "nav-nyaya" },
  { to: "/app/sentinel", label: "Sentinel", icon: Video, testId: "nav-sentinel" },
];

function NavGroup({ label, items, defaultOpen = true, onNavClick }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] tracking-widest uppercase text-[#4A5568] hover:text-[#2A0F5A]"
        data-testid={`navgroup-${label.toLowerCase()}-toggle`}
      >
        <span>{label}</span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && (
        <div className="space-y-0.5">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testId}
                onClick={onNavClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-[#2A0F5A] text-[#FAFAF8]"
                      : "text-[#2A0F5A]/75 hover:bg-[#FAFAF8] hover:text-[#2A0F5A]"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.5} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [openMobile, setOpenMobile] = React.useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="label-eyebrow">Loading Marianne…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const closeMobile = () => setOpenMobile(false);

  return (
    <div className="min-h-screen flex bg-[#FAFAF8] text-[#2A0F5A]">
      <aside
        className={`${openMobile ? "flex" : "hidden"} md:flex flex-col w-64 shrink-0 bg-white border-r border-[#E8E3F0] fixed md:sticky top-0 h-screen z-40`}
        data-testid="app-sidebar"
      >
        <div className="px-6 py-6 border-b border-[#E8E3F0] flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2.5" data-testid="sidebar-brand">
            <Logo size={28} />
            <span className="font-serif text-2xl leading-none">Marianne</span>
          </Link>
          <button onClick={closeMobile} className="md:hidden" data-testid="close-sidebar-btn">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
          <button
            onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
            className="w-full flex items-center gap-2 text-xs text-[#4A5568] hover:text-[#2A0F5A] px-3 py-2 border border-[#E8E3F0] bg-[#FAFAF8] hover:bg-white"
            data-testid="sidebar-search-hint"
          >
            <span className="text-[10px]">Search Marianne</span>
            <span className="ml-auto text-[10px] font-mono">⌘K</span>
          </button>
          <NavGroup label="Workspace" items={primaryNav} onNavClick={closeMobile} />
          <NavGroup label="Library" items={libraryNav} onNavClick={closeMobile} />
          <NavGroup label="Add-ons" items={addonNav} defaultOpen={false} onNavClick={closeMobile} />
          <div>
            <NavLink
              to="/app/assistant"
              onClick={closeMobile}
              data-testid="nav-assistant"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[#2A0F5A] text-[#FAFAF8]"
                    : "text-[#2A0F5A]/75 hover:bg-[#FAFAF8] hover:text-[#2A0F5A]"
                }`
              }
            >
              <Sparkles size={16} strokeWidth={1.5} />
              <span>Assistant</span>
            </NavLink>
            <NavLink
              to="/app/settings"
              onClick={closeMobile}
              data-testid="nav-settings"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[#2A0F5A] text-[#FAFAF8]"
                    : "text-[#2A0F5A]/75 hover:bg-[#FAFAF8] hover:text-[#2A0F5A]"
                }`
              }
            >
              <SettingsIcon size={16} strokeWidth={1.5} />
              <span>Settings</span>
            </NavLink>
          </div>
        </nav>

        <div className="border-t border-[#E8E3F0] p-4 space-y-2">
          <div className="text-xs">
            <div className="font-medium truncate" data-testid="sidebar-user-name">{user.full_name}</div>
            <div className="text-[#4A5568] truncate" data-testid="sidebar-user-email">{user.email}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-none border-[#E8E3F0] hover:border-[#2A0F5A]"
            onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-btn"
          >
            <LogOut size={14} strokeWidth={1.5} />
            <span className="ml-2">Sign out</span>
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col md:ml-0">
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[#E8E3F0] bg-white sticky top-0 z-30">
          <button onClick={() => setOpenMobile(true)} data-testid="open-sidebar-btn">
            <Menu size={20} />
          </button>
          <Link to="/app" className="font-serif text-xl">Marianne</Link>
          <div className="w-5" />
        </header>

        <main className="flex-1 p-4 md:p-10">
          <Outlet />
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}
